export * from "./types";
export * from "./constants";
export * from "./predictor";
export * from "./state-updater";
export * from "./session-builder";

import { prisma } from "@/lib/db";
import {
  checkFocusMastery,
  getSetCounts,
  promoteFocusSet,
  checkAndPerformGraduation,
  checkAndPerformPhaseTransition,
  getBoxStatus,
  getUserPhase,
} from "./predictor";
import { checkAnswer, updateStateAfterAnswer, updateStatePhase2 } from "./state-updater";
import { LEITNER_INTERVALS } from "./constants";
import type { FocusSetStatus, PresentedType, Phase, BoxStatus } from "./types";

export interface AnswerInput {
  userId: string;
  problemId: string;
  sessionId: string;
  userInput: string;
  responseMs: number;
  presentedType: PresentedType;
}

export interface RecordAnswerResult {
  wasCorrect: boolean;
  correctAnswer: string;
  answerDescription: string;
  answerImage?: string | null;
  focusStatus: FocusSetStatus;
  justMastered: boolean;  // Legacy: now means "just graduated"
  newFocusCount: number;
  // New fields
  boxUpdate?: {
    oldBox: number;
    newBox: number;
    promoted: boolean;
    demoted: boolean;
  };
  graduated?: boolean;
  phaseTransition?: boolean;
}

/**
 * Record an answer and update all relevant state
 */
export async function recordAnswer(
  input: AnswerInput
): Promise<RecordAnswerResult> {
  // Get problem
  const problem = await prisma.problem.findUnique({
    where: { id: input.problemId },
  });

  if (!problem) {
    throw new Error(`Problem not found: ${input.problemId}`);
  }

  // Determine correctness
  const wasCorrect = checkAnswer(input.userInput, problem.answer);

  // Get user phase
  const { currentPhase } = await getUserPhase(input.userId);

  let boxUpdate: RecordAnswerResult["boxUpdate"];
  let graduated = false;
  let phaseTransition = false;
  let newFocusCount = 0;

  if (currentPhase === 1) {
    // Phase 1: Update box state
    const result = await updateStateAfterAnswer(
      input.userId,
      input.problemId,
      input.sessionId,
      wasCorrect
    );

    boxUpdate = {
      oldBox: result.oldBox,
      newBox: result.newBox,
      promoted: result.promoted,
      demoted: result.demoted,
    };

    // Check for graduation
    if (wasCorrect) {
      const graduationResult = await checkAndPerformGraduation(input.userId);
      if (graduationResult.graduated) {
        graduated = true;
        newFocusCount = graduationResult.newCardsAdded;
      }

      // Check for phase transition
      const transitionResult = await checkAndPerformPhaseTransition(input.userId);
      if (transitionResult.transitioned) {
        phaseTransition = true;
      }
    }
  } else {
    // Phase 2: Update with Leitner intervals
    const currentState = await prisma.userProblemState.findUnique({
      where: {
        userId_problemId: { userId: input.userId, problemId: input.problemId },
      },
    });

    const currentBox = currentState?.box ?? 1;
    const newBox = wasCorrect ? Math.min(5, currentBox + 1) : 1;
    const intervalDays = LEITNER_INTERVALS[newBox] || 1;

    await updateStatePhase2(input.userId, input.problemId, wasCorrect, intervalDays);

    boxUpdate = {
      oldBox: currentBox,
      newBox,
      promoted: newBox > currentBox,
      demoted: newBox < currentBox,
    };
  }

  // Create review log
  await prisma.reviewLog.create({
    data: {
      userId: input.userId,
      problemId: input.problemId,
      sessionId: input.sessionId,
      presentedType: input.presentedType,
      userInput: input.userInput,
      wasCorrect,
      grade: wasCorrect ? "good" : "again",
      responseMs: input.responseMs,
    },
  });

  // Update session stats
  await prisma.studySession.update({
    where: { id: input.sessionId },
    data: {
      completedCount: { increment: 1 },
      correctCount: wasCorrect ? { increment: 1 } : undefined,
    },
  });

  // Get updated counts (legacy compatibility)
  const counts = await getSetCounts(input.userId);
  const masteryPercent = counts.focusCount > 0
    ? Math.round((counts.focusCorrectCount / counts.focusCount) * 100)
    : 0;

  return {
    wasCorrect,
    correctAnswer: problem.answer,
    answerDescription: problem.answerDescription,
    answerImage: problem.answerImage,
    focusStatus: {
      ...counts,
      masteryPercent,
      mastered: graduated,
      boxStatus: await getBoxStatus(input.userId),
    },
    justMastered: graduated,
    newFocusCount,
    boxUpdate,
    graduated,
    phaseTransition,
  };
}

/**
 * Get user's study statistics
 */
export async function getUserStats(userId: string) {
  const now = new Date();
  const startOfToday = new Date(now);
  startOfToday.setHours(0, 0, 0, 0);

  const [totalProblems, counts, todayLogs, boxStatus] = await Promise.all([
    prisma.problem.count(),
    getSetCounts(userId),
    prisma.reviewLog.findMany({
      where: { userId, timestamp: { gte: startOfToday } },
    }),
    getBoxStatus(userId),
  ]);

  const todayCorrect = todayLogs.filter(l => l.wasCorrect).length;
  const todayTotal = todayLogs.length;

  return {
    totalProblems,
    newCount: counts.newCount,
    focusCount: counts.focusCount,
    focusCorrectCount: counts.focusCorrectCount,
    oldCount: counts.oldCount,
    masteryPercent: counts.focusCount > 0
      ? Math.round((counts.focusCorrectCount / counts.focusCount) * 100)
      : 0,
    todayStats: {
      answered: todayTotal,
      correct: todayCorrect,
      accuracy: todayTotal > 0 ? todayCorrect / todayTotal : 0,
    },
    boxStatus,
  };
}
